import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  ArrowRight,
  BadgeDollarSign,
  Bell,
  Check,
  ClipboardCheck,
  FileText,
  PackageSearch,
  ShieldCheck,
  ShoppingCart,
  Sparkles,
  Smartphone,
  TrendingUp,
  WalletCards,
  Zap,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useProBranding } from '@/lib/pro-branding';

const benefits = [
  'Commission claire sur chaque commande confirmee',
  'Prix pro pour proteger la marge de votre client',
  'Catalogue large sans perdre du temps chez plusieurs fournisseurs',
];

const metrics = [
  { label: 'Commission suivie', value: 'Claire', tone: 'success' },
  { label: 'Produits disponibles', value: '2 800+', tone: 'primary' },
  { label: 'Temps devis gagne', value: '15 min', tone: 'info' },
];

const activityBars = [
  { day: 'Lun', orders: 2, height: 42 },
  { day: 'Mar', orders: 4, height: 68 },
  { day: 'Mer', orders: 3, height: 54 },
  { day: 'Jeu', orders: 6, height: 92 },
  { day: 'Ven', orders: 5, height: 76 },
  { day: 'Sam', orders: 7, height: 108 },
  { day: 'Dim', orders: 4, height: 66 },
];

const features = [
  {
    icon: BadgeDollarSign,
    title: 'Gagnez une commission visible',
    desc: 'Chaque ordre garde votre commission, son statut et son total. Vous savez exactement ce que vos chantiers rapportent.',
  },
  {
    icon: PackageSearch,
    title: 'Acces a une grande variete',
    desc: 'Tableaux, appareillage, cables, rangement et accessoires: cherchez, comparez et composez un panier complet au meme endroit.',
  },
  {
    icon: ClipboardCheck,
    title: 'Devis propres pour vos clients',
    desc: 'Preparez un devis rapide, transformez-le en commande et gardez une trace claire pour chaque client.',
  },
  {
    icon: ShieldCheck,
    title: 'Meilleurs prix, moins de friction',
    desc: 'Vous gardez une offre competitive pour le client tout en securisant votre marge et votre relation terrain.',
  },
];

const steps = [
  {
    title: 'Trouvez les produits',
    desc: 'Cherchez les references et familles dont vous avez besoin pour le chantier.',
  },
  {
    title: 'Envoyez un devis clair',
    desc: 'Ajoutez les produits, appliquez les bons prix et partagez une proposition professionnelle.',
  },
  {
    title: 'Suivez votre commission',
    desc: 'Une fois la commande confirmee, le portail garde le suivi de votre gain.',
  },
];

const faq = [
  {
    q: 'Est-ce que je vois ma commission avant de commander ?',
    a: 'Oui. Le portail affiche le montant estime et garde le suivi dans vos ordres et votre portefeuille.',
  },
  {
    q: 'Est-ce que je peux preparer un devis pour mon client ?',
    a: 'Oui. Vous selectionnez les produits, creez un devis propre, puis vous le transformez en commande quand le client confirme.',
  },
  {
    q: 'Je travaille deja avec plusieurs fournisseurs. Pourquoi changer ?',
    a: 'Tunielec Pro reduit les allers-retours: plus de choix, prix pro, suivi de commandes et commission dans un seul espace.',
  },
];

export function LandingPage() {
  const { loginLogoUrl } = useProBranding();

  return (
    <div className="min-h-screen bg-[var(--pro-bg)] text-[var(--pro-text-dark)]">
      <LandingNav logoUrl={loginLogoUrl} />
      <main>
        <HeroSection logoUrl={loginLogoUrl} />
        <TrustStrip />
        <FeatureSection />
        <CommissionSection />
        <StepsSection />
        <MobileAppSection />
        <FaqSection />
        <FinalCta logoUrl={loginLogoUrl} />
      </main>
      <Footer />
    </div>
  );
}

function LandingNav({ logoUrl }: { logoUrl: string | null }) {
  return (
    <header className="sticky top-0 z-40 border-b border-white/70 bg-[var(--pro-bg)]/90 backdrop-blur-xl">
      <div className="mx-auto flex h-20 w-full max-w-7xl items-center justify-between px-5 sm:px-8">
        <Link to="/" className="flex items-center gap-3" aria-label="Tunielec Pro">
          {logoUrl ? (
            <img src={logoUrl} alt="Tunielec Pro" className="h-10 w-auto max-w-[178px] object-contain" />
          ) : (
            <Wordmark />
          )}
        </Link>
        <nav className="hidden items-center gap-7 text-sm font-semibold text-[var(--pro-text-muted)] md:flex">
          <a href="#gains" className="hover:text-[var(--pro-primary)]">Gains</a>
          <a href="#catalogue" className="hover:text-[var(--pro-primary)]">Catalogue</a>
          <a href="#comment-ca-marche" className="hover:text-[var(--pro-primary)]">Comment ca marche</a>
        </nav>
        <div className="flex items-center gap-2">
          <Link
            to="/login"
            className="hidden h-11 items-center justify-center rounded-xl px-4 text-sm font-semibold text-[var(--pro-text-muted)] hover:bg-white sm:inline-flex"
          >
            Connexion
          </Link>
          <Link
            to="/register"
            className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-[var(--pro-primary)] px-4 text-sm font-semibold text-white shadow-[0_10px_24px_rgba(255,107,30,0.28)] hover:bg-[var(--pro-primary-hover)]"
          >
            Devenir partenaire
            <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </header>
  );
}

function HeroSection({ logoUrl }: { logoUrl: string | null }) {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 bg-white/45" />
      <div className="relative mx-auto w-full max-w-7xl px-5 pb-12 pt-14 sm:px-8 sm:pb-16 lg:pt-20">
        <div className="mx-auto max-w-4xl text-center">
          <div className="mx-auto mb-5 inline-flex items-center gap-2 rounded-full border border-[var(--pro-primary-light)] bg-white px-4 py-2 text-sm font-semibold text-[var(--pro-primary)] shadow-sm">
            <Sparkles size={15} />
            Portail pro dedie aux electriciens
          </div>
          <h1 className="text-5xl font-bold leading-[1.02] sm:text-6xl lg:text-7xl">
            Gagnez plus sur chaque chantier electrique
          </h1>
          <p className="mx-auto mt-6 max-w-3xl text-lg leading-8 text-[var(--pro-text-muted)] sm:text-xl">
            Accedez aux prix pro, preparez des devis rapides et suivez vos
            commissions jusqu'a la commande dans un seul espace Tunielec Pro.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              to="/register"
              className="inline-flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-[var(--pro-primary)] px-7 text-base font-semibold text-white shadow-[0_18px_38px_rgba(255,107,30,0.32)] hover:bg-[var(--pro-primary-hover)] sm:w-auto"
            >
              Ouvrir mon espace pro
              <ArrowRight size={18} />
            </Link>
            <Link
              to="/login"
              className="inline-flex h-14 w-full items-center justify-center rounded-2xl border border-[var(--pro-line)] bg-white px-7 text-base font-semibold text-[var(--pro-text-dark)] hover:border-[var(--pro-primary-light)] sm:w-auto"
            >
              J'ai deja un compte
            </Link>
          </div>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm font-medium text-[var(--pro-text-light)]">
            {benefits.map((benefit) => (
              <span key={benefit} className="inline-flex items-center gap-1.5">
                <Check size={15} className="text-[var(--pro-success)]" />
                {benefit}
              </span>
            ))}
          </div>
        </div>

        <div className="mx-auto mt-12 max-w-6xl">
          <OpportunityBoard logoUrl={logoUrl} />
        </div>
      </div>
    </section>
  );
}

function TrustStrip() {
  return (
    <section className="border-y border-[var(--pro-line)] bg-white">
      <div className="mx-auto grid max-w-7xl grid-cols-1 divide-y divide-[var(--pro-line)] px-5 sm:px-8 md:grid-cols-3 md:divide-x md:divide-y-0">
        {metrics.map((metric) => (
          <div key={metric.label} className="py-7 text-center">
            <div className={metricClass(metric.tone)}>{metric.value}</div>
            <div className="mt-1 text-sm font-medium text-[var(--pro-text-muted)]">{metric.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function FeatureSection() {
  return (
    <section id="catalogue" className="px-5 py-16 sm:px-8 lg:py-24">
      <div className="mx-auto max-w-7xl">
        <SectionIntro
          eyebrow="Une seule place pour vendre mieux"
          title="Moins de temps a chercher, plus de marge a proteger"
          text="Tunielec Pro transforme le catalogue fournisseur en outil commercial pour l'electricien: vous preparez vite, vous gardez le bon prix, et vous suivez votre gain."
        />
        <div className="mt-12 grid gap-5 md:grid-cols-2">
          {features.map((feature) => (
            <article key={feature.title} className="pro-card pro-card-hoverable p-6 sm:p-7">
              <div className="flex items-start gap-4">
                <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-[var(--pro-primary-subtle)] text-[var(--pro-primary)]">
                  <feature.icon size={22} />
                </span>
                <div>
                  <h3 className="text-2xl font-bold">{feature.title}</h3>
                  <p className="mt-3 text-base leading-7 text-[var(--pro-text-muted)]">{feature.desc}</p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function CommissionSection() {
  return (
    <section id="gains" className="bg-white px-5 py-16 sm:px-8 lg:py-24">
      <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
        <div>
          <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-[var(--pro-primary-subtle)] px-4 py-2 text-sm font-semibold text-[var(--pro-primary)]">
            <TrendingUp size={15} />
            Pense pour votre rentabilite
          </div>
          <h2 className="text-4xl font-bold leading-tight sm:text-5xl">
            Chaque devis peut devenir une nouvelle commission.
          </h2>
          <p className="mt-5 text-lg leading-8 text-[var(--pro-text-muted)]">
            Au lieu de perdre la marge dans les appels, les captures et les prix changes,
            vous construisez votre panier dans un espace clair et vous transformez plus vite
            les demandes client en commandes suivies.
          </p>
          <div className="mt-8 grid gap-3 sm:grid-cols-2">
            <ValuePill icon={WalletCards}>Commission lisible</ValuePill>
            <ValuePill icon={ShoppingCart}>Commande centralisee</ValuePill>
            <ValuePill icon={FileText}>Devis professionnel</ValuePill>
            <ValuePill icon={Zap}>Reponse client rapide</ValuePill>
          </div>
        </div>
        <div className="pro-card overflow-hidden p-0">
          <div className="border-b border-[var(--pro-line)] px-6 py-5">
            <div className="text-sm font-semibold text-[var(--pro-text-muted)]">Simulation chantier</div>
            <div className="mt-1 flex flex-wrap items-center justify-between gap-3">
              <div className="text-2xl font-bold">Maison Sfax Nord</div>
              <span className="rounded-full bg-[var(--pro-primary-subtle)] px-3 py-1 text-sm font-bold text-[var(--pro-primary)]">
                Commission 5%
              </span>
            </div>
          </div>
          <div className="divide-y divide-[var(--pro-line)]">
            <SummaryRow label="Total produits" value="1 860,500 DT" />
            <SummaryRow label="Prix client avec commission" value="1 953,525 DT" />
            <SummaryRow label="Commission electricien" value="93,025 DT" strong />
          </div>
          <div className="bg-[var(--pro-primary-subtle)] px-6 py-5">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="text-sm font-semibold text-[var(--pro-primary)]">Action recommandee</div>
                <div className="mt-1 text-base font-bold text-[var(--pro-text-dark)]">Envoyer le devis aujourd'hui</div>
              </div>
              <Link
                to="/register"
                className="inline-flex h-11 shrink-0 items-center justify-center rounded-xl bg-[var(--pro-primary)] px-4 text-sm font-semibold text-white hover:bg-[var(--pro-primary-hover)]"
              >
                Demarrer
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function StepsSection() {
  return (
    <section id="comment-ca-marche" className="px-5 py-16 sm:px-8 lg:py-24">
      <div className="mx-auto max-w-7xl">
        <SectionIntro
          eyebrow="Simple sur le terrain"
          title="Du besoin client a la commande en trois etapes"
          text="Le portail garde le flux direct: trouver, chiffrer, confirmer. Vous restez concentre sur le chantier."
        />
        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {steps.map((step, index) => (
            <article key={step.title} className="pro-card p-7">
              <div className="grid h-12 w-12 place-items-center rounded-2xl bg-white text-xl font-bold text-[var(--pro-primary)] shadow-sm">
                {index + 1}
              </div>
              <h3 className="mt-6 text-2xl font-bold">{step.title}</h3>
              <p className="mt-3 text-base leading-7 text-[var(--pro-text-muted)]">{step.desc}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function MobileAppSection() {
  return (
    <section className="bg-[#f9f4ef] px-5 py-16 sm:px-8 lg:py-24">
      <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
        <div>
          <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-semibold text-[var(--pro-primary)]">
            <Smartphone size={16} />
            Bientot sur mobile
          </div>
          <h2 className="max-w-3xl text-4xl font-bold leading-tight sm:text-5xl">
            Votre espace pro arrive aussi sur Android et iOS.
          </h2>
          <p className="mt-5 max-w-2xl text-lg leading-8 text-[var(--pro-text-muted)]">
            Consultez vos devis, suivez vos commandes et gardez un oeil sur vos
            commissions directement depuis le terrain.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <PlatformPill>Android bientot disponible</PlatformPill>
            <PlatformPill>iOS bientot disponible</PlatformPill>
          </div>
        </div>

        <div className="relative mx-auto flex min-h-[560px] w-full max-w-xl items-center justify-center">
          <div className="absolute left-8 top-16 hidden h-[370px] w-[220px] rotate-[-8deg] rounded-[38px] border border-[var(--pro-line)] bg-white p-3 opacity-90 sm:block">
            <div className="h-full rounded-[26px] bg-white p-3">
              <div className="mb-4 h-4 w-16 rounded-full bg-[var(--pro-primary)]" />
              <div className="space-y-2">
                <div className="rounded-2xl bg-[#f9f4ef] p-3">
                  <div className="text-xs font-bold uppercase text-[var(--pro-text-light)]">Commande</div>
                  <div className="mt-1 text-lg font-bold">ORD-2026-002</div>
                  <div className="mt-2 text-xs text-[var(--pro-text-muted)]">Client chantier Sfax</div>
                </div>
                <MobilePreviewRow label="Prix client" value="1 953,525 DT" />
                <MobilePreviewRow label="Commission" value="93,025 DT" active />
              </div>
            </div>
          </div>

          <div className="relative z-10 w-[270px] rounded-[42px] border-[8px] border-[var(--pro-text-dark)] bg-[var(--pro-text-dark)] shadow-[0_24px_60px_rgba(51,51,51,0.16)]">
            <div className="absolute left-1/2 top-2.5 z-20 h-4 w-20 -translate-x-1/2 rounded-full bg-[var(--pro-text-dark)]" />
            <div className="h-[570px] overflow-hidden rounded-[32px] bg-[#f9f4ef]">
              <div className="bg-white px-4 pb-4 pt-7">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.16em] text-[var(--pro-text-light)]">Tunielec Pro</div>
                    <div className="mt-0.5 text-lg font-bold">Bonjour Mohamed</div>
                  </div>
                  <div className="relative grid h-9 w-9 place-items-center rounded-full bg-[var(--pro-primary)] text-xs font-bold text-white">
                    MB
                    <span className="absolute -right-1 -top-1 h-3 w-3 rounded-full border-2 border-white bg-[var(--pro-success)]" />
                  </div>
                </div>
                <div className="mt-3 rounded-3xl bg-[var(--pro-primary)] p-3 text-white">
                  <div className="flex items-center gap-2 text-xs font-semibold text-white/80">
                    <WalletCards size={14} />
                    Commission disponible
                  </div>
                  <div className="mt-1 text-2xl font-bold tabular-nums">93,025 DT</div>
                  <div className="mt-2 h-1.5 rounded-full bg-white/20">
                    <div className="h-1.5 w-4/5 rounded-full bg-white" />
                  </div>
                </div>
              </div>

              <div className="px-4 py-4">
                <div className="grid grid-cols-2 gap-2">
                  <MobileMiniStat label="Devis" value="12" />
                  <MobileMiniStat label="Ordres" value="8" />
                </div>

                <div className="mt-3 rounded-3xl bg-white p-3">
                  <div className="mb-3 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-bold">Activite semaine</div>
                      <div className="text-xs text-[var(--pro-text-light)]">Commandes confirmees</div>
                    </div>
                    <span className="rounded-full bg-[var(--pro-primary-subtle)] px-2 py-1 text-xs font-bold text-[var(--pro-primary)]">+5%</span>
                  </div>
                  <div className="flex h-16 items-end gap-2">
                    {[22, 34, 28, 48, 40, 58].map((height, index) => (
                      <div key={index} className="flex-1 rounded-t-xl bg-[linear-gradient(180deg,#ffb27d_0%,var(--pro-primary)_100%)]" style={{ height: `${height}px` }} />
                    ))}
                  </div>
                </div>

                <div className="mt-4 space-y-3">
                  <MobileOrderCard title="Client chantier Sfax" amount="1 953,525 DT" status="En attente" />
                  <MobileOrderCard title="Villa route Teniour" amount="760,000 DT" status="Devis pret" />
                </div>
              </div>

              <div className="mx-4 mb-4 grid grid-cols-4 rounded-3xl bg-white p-1.5 text-[10px] font-bold text-[var(--pro-text-light)]">
                <span className="rounded-2xl bg-[var(--pro-primary-subtle)] px-2 py-2 text-center text-[var(--pro-primary)]">Home</span>
                <span className="px-2 py-2 text-center">Devis</span>
                <span className="px-2 py-2 text-center">Ordres</span>
                <span className="px-2 py-2 text-center">Wallet</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PlatformPill({ children }: { children: ReactNode }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-2xl border border-[var(--pro-line)] bg-white px-4 py-3 text-sm font-bold">
      <Check size={16} className="text-[var(--pro-success)]" />
      {children}
    </div>
  );
}

function MobilePreviewRow({ label, value, active }: { label: string; value: string; active?: boolean }) {
  return (
    <div className={`flex items-center justify-between rounded-2xl p-4 ${active ? 'bg-[var(--pro-primary-subtle)]' : 'bg-white'}`}>
      <span className="text-sm font-semibold text-[var(--pro-text-muted)]">{label}</span>
      <span className="font-bold tabular-nums text-[var(--pro-text-dark)]">{value}</span>
    </div>
  );
}

function MobileMiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-3xl bg-white p-3">
      <div className="text-xs font-bold uppercase text-[var(--pro-text-light)]">{label}</div>
      <div className="mt-1 text-xl font-bold text-[var(--pro-text-dark)]">{value}</div>
    </div>
  );
}

function MobileOrderCard({ title, amount, status }: { title: string; amount: string; status: string }) {
  return (
    <div className="rounded-3xl bg-white p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-bold">{title}</div>
          <div className="mt-1 text-xs text-[var(--pro-text-light)]">{status}</div>
        </div>
        <div className="text-right text-xs font-bold tabular-nums">{amount}</div>
      </div>
    </div>
  );
}

function FaqSection() {
  return (
    <section className="bg-white px-5 py-16 sm:px-8 lg:py-24">
      <div className="mx-auto max-w-4xl">
        <SectionIntro
          eyebrow="Questions frequentes"
          title="Ce que les electriciens demandent avant de rejoindre"
          text="Tunielec Pro est fait pour vendre plus vite, pas pour ajouter de la complexite."
        />
        <div className="mt-10 divide-y divide-[var(--pro-line)] rounded-3xl border border-[var(--pro-line)] bg-white">
          {faq.map((item) => (
            <details key={item.q} className="group p-6">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-left text-lg font-bold">
                {item.q}
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[var(--pro-primary-subtle)] text-[var(--pro-primary)] group-open:rotate-45">
                  +
                </span>
              </summary>
              <p className="mt-4 max-w-3xl text-base leading-7 text-[var(--pro-text-muted)]">{item.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCta({ logoUrl }: { logoUrl: string | null }) {
  return (
    <section className="px-5 py-16 sm:px-8 lg:py-24">
      <div className="mx-auto max-w-5xl overflow-hidden rounded-[32px] bg-[var(--pro-text-dark)] px-6 py-12 text-center text-white sm:px-10">
        {logoUrl ? (
          <img src={logoUrl} alt="Tunielec Pro" className="mx-auto h-12 w-auto max-w-[220px] object-contain brightness-0 invert" />
        ) : (
          <div className="inline-flex text-2xl font-bold">Tunielec Pro</div>
        )}
        <h2 className="mx-auto mt-7 max-w-3xl text-4xl font-bold leading-tight sm:text-5xl">
          Transformez vos demandes client en commandes rentables.
        </h2>
        <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-white/72">
          Rejoignez l'espace pro, preparez vos devis plus vite et gardez le suivi de vos commissions dans un seul portail.
        </p>
        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link
            to="/register"
            className="inline-flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-[var(--pro-primary)] px-7 text-base font-semibold text-white hover:bg-[var(--pro-primary-hover)] sm:w-auto"
          >
            Creer mon compte pro
            <ArrowRight size={18} />
          </Link>
          <Link
            to="/login"
            className="inline-flex h-14 w-full items-center justify-center rounded-2xl border border-white/18 px-7 text-base font-semibold text-white hover:bg-white/10 sm:w-auto"
          >
            Connexion
          </Link>
        </div>
      </div>
    </section>
  );
}

function OpportunityBoard({ logoUrl }: { logoUrl: string | null }) {
  return (
    <div className="overflow-hidden rounded-[28px] border border-white bg-white">
      <div className="grid min-h-[500px] bg-white lg:grid-cols-[220px_1fr]">
        <aside className="hidden border-r border-[var(--pro-line)] bg-white p-5 lg:block">
          {logoUrl ? (
            <img src={logoUrl} alt="Tunielec Pro" className="h-10 w-auto max-w-[150px] object-contain" />
          ) : (
            <div className="flex items-center gap-2">
              <span className="grid h-9 w-9 place-items-center rounded-xl bg-[var(--pro-primary)] text-lg font-black text-white">
                T
              </span>
              <div>
                <div className="text-sm font-bold">Tunielec Pro</div>
                <div className="text-xs text-[var(--pro-text-light)]">Espace electricien</div>
              </div>
            </div>
          )}
          <nav className="mt-7 space-y-2 text-sm font-semibold">
            <DashboardNavItem icon={TrendingUp} label="Tableau de bord" active />
            <DashboardNavItem icon={FileText} label="Devis" />
            <DashboardNavItem icon={ShoppingCart} label="Ordres" />
            <DashboardNavItem icon={PackageSearch} label="Catalogue" />
            <DashboardNavItem icon={WalletCards} label="Portefeuille" />
          </nav>
        </aside>

        <div className="bg-[#f9f4ef] p-4 sm:p-6">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.16em] text-[var(--pro-text-light)]">Aujourd'hui</div>
              <h3 className="mt-1 text-2xl font-bold tracking-tight">Tableau de bord pro</h3>
            </div>
            <DashboardHeaderActions />
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <DashboardMetric title="Devis en cours" value="12" tone="primary" />
            <DashboardMetric title="Commandes confirmees" value="8" tone="success" />
            <DashboardMetric title="Clients actifs" value="24" tone="info" />
          </div>

          <div className="mt-3 grid gap-3 xl:grid-cols-[1.25fr_0.75fr]">
            <div className="rounded-2xl border border-[var(--pro-line)] bg-white p-5">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h4 className="font-bold">Activite des commandes</h4>
                  <p className="mt-1 text-sm text-[var(--pro-text-muted)]">Devis envoyes, commandes et commissions suivies</p>
                </div>
                <span className="rounded-full bg-[var(--pro-primary-subtle)] px-3 py-1 text-xs font-bold text-[var(--pro-primary)]">
                  Cette semaine
                </span>
              </div>
              <div className="mt-5 grid grid-cols-3 gap-2 text-center">
                <MiniActivityStat label="Devis" value="12" />
                <MiniActivityStat label="Commandes" value="8" />
                <MiniActivityStat label="Commission" value="93 DT" />
              </div>
              <div className="mt-6 flex h-40 items-end gap-2 border-b border-[var(--pro-line)] px-1">
                {activityBars.map((item) => (
                  <div key={item.day} className="flex flex-1 flex-col items-center gap-2">
                    <span className="text-[10px] font-bold text-[var(--pro-primary)]">{item.orders}</span>
                    <div
                      className="w-full rounded-t-xl"
                      style={{
                        height: `${item.height}px`,
                        background: 'linear-gradient(180deg, #ffb27d 0%, var(--pro-primary) 100%)',
                      }}
                    />
                    <span className="text-[10px] font-semibold text-[var(--pro-text-light)]">{item.day}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-[var(--pro-line)] bg-white p-5">
              <h4 className="font-bold">Portefeuille</h4>
              <div className="mt-5 rounded-2xl bg-[var(--pro-primary-subtle)] p-4">
                <div className="text-sm font-semibold text-[var(--pro-primary)]">Commission disponible</div>
                <div className="mt-2 text-3xl font-bold tabular-nums text-[var(--pro-text-dark)]">93,025 DT</div>
                <div className="mt-3 text-sm text-[var(--pro-text-muted)]">apres confirmation commande</div>
              </div>
              <div className="mt-4 space-y-3">
                <ProgressLine label="Devis valides" value="72%" />
                <ProgressLine label="Ordres livres" value="64%" />
                <ProgressLine label="Commissions suivies" value="81%" />
              </div>
            </div>
          </div>

          <div className="mt-3 rounded-2xl border border-[var(--pro-line)] bg-white p-5">
            <div className="mb-4 flex items-center justify-between">
              <h4 className="font-bold">Ordres recents</h4>
              <span className="text-xs font-semibold text-[var(--pro-text-light)]">Suivi client</span>
            </div>
            <div className="space-y-2">
              <DashboardOrder id="ORD-2026-002" client="Client chantier Sfax" amount="1 953,525 DT" status="En attente" />
              <DashboardOrder id="DEV-2026-018" client="Villa route Teniour" amount="760,000 DT" status="Devis pret" />
              <DashboardOrder id="ORD-2026-001" client="Maintenance atelier" amount="428,500 DT" status="Confirmee" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DashboardNavItem({
  icon: Icon,
  label,
  active,
}: {
  icon: LucideIcon;
  label: string;
  active?: boolean;
}) {
  return (
    <div
      className={`flex items-center gap-3 rounded-2xl px-3 py-2.5 ${
        active
          ? 'bg-[var(--pro-primary-subtle)] text-[var(--pro-primary)]'
          : 'text-[var(--pro-text-muted)]'
      }`}
    >
      <Icon size={16} />
      <span>{label}</span>
    </div>
  );
}

function DashboardHeaderActions() {
  return (
    <div className="flex items-center gap-2">
      <div className="hidden h-10 items-center gap-2 rounded-full border-2 border-[var(--pro-primary)] bg-white px-4 text-sm font-bold text-[var(--pro-text-dark)] sm:flex">
        <WalletCards size={16} className="text-[var(--pro-primary)]" />
        <span className="text-[var(--pro-text-muted)]">Commission</span>
        <span>93,025 DT</span>
      </div>
      <button type="button" aria-label="Panier" className="relative grid h-10 w-10 place-items-center rounded-full border border-[var(--pro-line)] bg-white text-[var(--pro-text-muted)]">
        <ShoppingCart size={17} />
        <span className="absolute -right-0.5 -top-1 grid h-4 w-4 place-items-center rounded-full bg-[var(--pro-primary)] text-[10px] font-bold text-white">2</span>
      </button>
      <button type="button" aria-label="Notifications" className="grid h-10 w-10 place-items-center rounded-full border border-[var(--pro-line)] bg-white text-[var(--pro-text-muted)]">
        <Bell size={17} />
      </button>
      <div className="grid h-10 w-10 place-items-center rounded-full bg-[var(--pro-primary)] text-sm font-bold text-white">
        MB
      </div>
    </div>
  );
}

function DashboardMetric({ title, value, tone }: { title: string; value: string; tone: string }) {
  return (
    <div className="rounded-2xl border border-[var(--pro-line)] bg-white p-4">
      <div className="text-sm font-semibold text-[var(--pro-text-muted)]">{title}</div>
      <div className={metricClass(tone).replace('text-4xl', 'mt-4 text-3xl')}>{value}</div>
      <div className="mt-3 h-1.5 rounded-full bg-[var(--pro-grey-subtle)]">
        <div className="h-1.5 w-2/3 rounded-full bg-[var(--pro-primary)]" />
      </div>
    </div>
  );
}

function MiniActivityStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl bg-[#f9f4ef] px-3 py-2">
      <div className="text-[10px] font-semibold uppercase text-[var(--pro-text-light)]">{label}</div>
      <div className="mt-1 text-sm font-bold text-[var(--pro-text-dark)]">{value}</div>
    </div>
  );
}

function DashboardOrder({ id, client, amount, status }: { id: string; client: string; amount: string; status: string }) {
  return (
    <div className="grid grid-cols-[1fr_auto] gap-3 rounded-2xl bg-[var(--pro-bg)] px-4 py-3 text-sm sm:grid-cols-[150px_1fr_120px_140px]">
      <div className="font-bold text-[var(--pro-text-dark)]">{id}</div>
      <div className="min-w-0 truncate text-[var(--pro-text-muted)]">{client}</div>
      <div className="hidden text-right font-bold tabular-nums text-[var(--pro-text-dark)] sm:block">{amount}</div>
      <div className="justify-self-end rounded-full bg-white px-3 py-1 text-xs font-bold text-[var(--pro-primary)]">{status}</div>
    </div>
  );
}

function ProgressLine({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="mb-2 flex justify-between text-sm font-semibold">
        <span className="text-[var(--pro-text-muted)]">{label}</span>
        <span>{value}</span>
      </div>
      <div className="h-2 rounded-full bg-[var(--pro-grey-subtle)]">
        <div className="h-2 rounded-full bg-[var(--pro-primary)]" style={{ width: value }} />
      </div>
    </div>
  );
}

function SectionIntro({ eyebrow, title, text }: { eyebrow: string; title: string; text: string }) {
  return (
    <div className="mx-auto max-w-3xl text-center">
      <div className="mx-auto mb-5 inline-flex items-center rounded-full bg-[var(--pro-primary-subtle)] px-4 py-2 text-sm font-semibold text-[var(--pro-primary)]">
        {eyebrow}
      </div>
      <h2 className="text-4xl font-bold leading-tight sm:text-5xl">{title}</h2>
      <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-[var(--pro-text-muted)]">{text}</p>
    </div>
  );
}

function ValuePill({ icon: Icon, children }: { icon: LucideIcon; children: ReactNode }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-[var(--pro-line)] bg-white px-4 py-3 text-sm font-semibold">
      <Icon size={18} className="text-[var(--pro-primary)]" />
      {children}
    </div>
  );
}

function SummaryRow({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-4 px-6 py-4">
      <span className="text-sm font-semibold text-[var(--pro-text-muted)]">{label}</span>
      <span className={`text-right font-bold tabular-nums ${strong ? 'text-xl text-[var(--pro-success)]' : 'text-[var(--pro-text-dark)]'}`}>
        {value}
      </span>
    </div>
  );
}

function Footer() {
  return (
    <footer className="border-t border-[var(--pro-line)] bg-white px-5 py-8 sm:px-8">
      <div className="mx-auto max-w-7xl text-sm text-[var(--pro-text-light)]">
        <span>Copyright (c) 2026 Tunielec Pro</span>
      </div>
    </footer>
  );
}

function Wordmark() {
  return (
    <div className="flex items-baseline gap-1.5">
      <span className="text-2xl font-bold leading-none text-[var(--pro-text-dark)]">Tunielec</span>
      <span className="text-2xl font-semibold italic leading-none text-[var(--pro-primary)]">Pro</span>
    </div>
  );
}

function metricClass(tone: string) {
  const base = 'text-4xl font-bold';
  if (tone === 'success') return `${base} text-[var(--pro-success)]`;
  if (tone === 'info') return `${base} text-[var(--pro-info)]`;
  return `${base} text-[var(--pro-primary)]`;
}
